// src/pages/matching-intergenerationnel/IntergenerationalMatchingTinder.jsx

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';


const IntergenerationalMatchingTinder = ({ artworks, heirs, onMatch, onReject }) => {
  const [artIndex, setArtIndex] = useState(0);
  const [heirIndex, setHeirIndex] = useState(0);

  const currentArt = artworks[artIndex];
  const currentHeir = heirs[heirIndex];

  const handleDecision = (accepted) => {
    if (!currentArt || !currentHeir) return;
    accepted ? onMatch(currentArt, currentHeir) : onReject(currentArt, currentHeir);
    if (heirIndex < heirs.length - 1) {
      setHeirIndex(prev => prev + 1);
    } else {
      setHeirIndex(0);
      setArtIndex(prev => prev + 1);
    }
  };

  if (artIndex >= artworks.length) {
    return <p className="text-center text-lg mt-8">All pairings are complete.</p>;
  }

  return (
    <div className="flex items-start justify-center h-full p-4 space-x-8">

      {/* Artwork Card */}
      <div className="w-1/3 bg-white rounded-2xl shadow-lg overflow-hidden">
        <img
          src={currentArt?.imageUrl || '/images/artworks/Vignette_oeuvre_Image_Not_Available.png'}
          alt={currentArt?.title}
          className="w-full h-48 object-cover"
          onError={(e) => {
            e.currentTarget.src = '/images/artworks/Vignette_oeuvre_Image_Not_Available.png';
          }}
        />
        <div className="p-4">
          <h2 className="text-xl font-semibold mb-2">{currentArt?.title}</h2>
          {currentArt?.year && <p className="text-sm text-gray-600">{currentArt.year}</p>}
          {currentArt?.medium && <p className="text-sm text-gray-600 mb-4">{currentArt.medium}</p>}
        </div>
      </div>

      {/* Heir Card with swipe */}
      <div className="w-1/3">
        <AnimatePresence>
          {currentHeir && (
            <motion.div
              key={currentHeir.id || 'unknown'}
              className="bg-gray-100 rounded-2xl shadow-lg overflow-hidden cursor-grab"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ x: 300, opacity: 0 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={(e, info) => {
                if (info.offset.x > 100) handleDecision(true);
                else if (info.offset.x < -100) handleDecision(false);
              }}
            >
              <img
                src={currentHeir.imageUrl || '/images/beneficiaries/silhouette_default.png'}
                alt={currentHeir.name || 'Heir'}
                className="w-32 h-32 object-cover rounded-full mx-auto mt-6 shadow-md border-2 border-gray-300 hover:scale-105 transition-transform"
                onError={(e) => {
                  e.currentTarget.src = '/images/beneficiaries/silhouette_default.png';
                }}
              />
              <div className="p-4 text-center">
                <h2 className="text-xl font-semibold">
                  {currentHeir.name || 'Heir'}{currentHeir.age ? `, ${currentHeir.age} years` : ''}
                </h2>
                <p className="mt-2 font-semibold">
                  Score: {currentHeir.score !== undefined ? `${currentHeir.score}%` : "Not evaluated"}
                </p>

                <div className="mt-2">
                  <p className="font-semibold">Preferences:</p>
                  <ul className="list-disc list-inside text-sm">
                    {(currentHeir.preferences || currentHeir.art_styles || []).map((pref, index) => 
                      <li key={`pref-${index}`}>{pref}</li>
                    )}
                  </ul>
                </div>
                <div className="mt-2">
                  <p className="font-semibold">Values:</p>
                  <ul className="list-disc list-inside text-sm">
                    {(currentHeir.values || []).map((val, index) => 
                      <li key={`val-${index}`}>{val}</li>
                    )}
                  </ul>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Click buttons */}
        {currentHeir && (
          <div className="flex justify-center space-x-4 mt-6">
            <button
              onClick={() => handleDecision(false)}
              className="px-6 py-2 rounded-full bg-gray-200"
            >
              Skip
            </button>
            <button
              onClick={() => handleDecision(true)}
              className="px-6 py-2 rounded-full bg-blue-500 text-white"
            >
              Transmit
            </button>
          </div>
        )}
      </div>

    </div>
  );
};

export default IntergenerationalMatchingTinder;